import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import { SearchIcon } from '@components/icon/svg';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Severity High
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Severity Low
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // Pain Point
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'; // Severity Moderate
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

// Helper for Severity
const getSeverityStyle = (severity) => {
  switch (severity?.toLowerCase()) {
    case 'high':
      return { icon: <ErrorOutlineIcon fontSize="small" />, color: 'error', label: 'High' };
    case 'moderate':
      return {
        icon: <WarningAmberOutlinedIcon fontSize="small" />,
        color: 'warning',
        label: 'Moderate',
      };
    case 'low':
      return { icon: <InfoOutlinedIcon fontSize="small" />, color: 'info', label: 'Low' };
    default:
      return { icon: null, color: 'default', label: severity || 'N/A' };
  }
};

const JourneyPainAnalysisTranscript = ({ summaryData }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!summaryData || !summaryData.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Journey Pain analysis data not available.
        </Box>
      </Card>
    );
  }

  const { summary, stages = [] } = summaryData.results;

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Customer Journey Pain Point Analysis
          <AnalysisLensHelperText lensName="journey_pain" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        {summary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Analysis Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          </Box>
        )}

        {/* Stages */}
        {stages.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Journey Stages & Pain Points
            </Typography>
            {stages.map((stage, index) => {
              const panelId = `panel${index}`;
              const severityStyle = getSeverityStyle(stage.severity);
              return (
                <Accordion
                  variant="outlined"
                  key={panelId}
                  expanded={expanded === panelId}
                  onChange={handleChange(panelId)}
                  TransitionProps={{ unmountOnExit: true }} // Optional: improves performance
                  sx={{
                    '&:before': { display: 'none' },
                    border: 1,
                    borderColor: 'divider',
                    '&:not(:last-child)': { borderBottom: 0 },
                    borderRadius: 1,
                    mb: 1.5,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${panelId}-content`}
                    id={`${panelId}-header`}
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 'inherit',
                      borderBottomLeftRadius: expanded === panelId ? 0 : 'inherit',
                      borderBottomRightRadius: expanded === panelId ? 0 : 'inherit',
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      width="100%"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {stage.stage}
                      </Typography>
                      <Chip
                        icon={severityStyle.icon}
                        label={`Severity: ${severityStyle.label}`}
                        color={severityStyle.color}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Stack spacing={2}>
                      {/* Pain Points */}
                      {stage.pain_points?.length > 0 && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'medium',
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <WarningAmberIcon fontSize="inherit" color="error" /> Pain Points:
                          </Typography>
                          <List dense disablePadding sx={{ pl: 1 }}>
                            {stage.pain_points.map((point, idx) => (
                              <ListItem key={`pain-${index}-${idx}`} disablePadding>
                                <ListItemText
                                  primary={`• ${point}`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Underlying Causes */}
                      {stage.underlying_causes?.length > 0 && (
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 'medium',
                              mb: 1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <SearchIcon fontSize="inherit" color="action" /> Underlying Causes:
                          </Typography>
                          <List dense disablePadding sx={{ pl: 1 }}>
                            {stage.underlying_causes.map((cause, idx) => (
                              <ListItem key={`cause-${index}-${idx}`} disablePadding>
                                <ListItemText
                                  primary={`• ${cause}`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {/* Quotes */}
                      {stage.participant_quotes?.length > 0 && (
                        <Box sx={{ pt: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={0.5}
                          >
                            Supporting Quotes:
                          </Typography>
                          <List dense disablePadding>
                            {stage.participant_quotes.map((quote, idx) => (
                              <ListItem
                                key={`quote-${index}-${idx}`}
                                disableGutters
                                sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 0.5 } }}
                              >
                                <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                                  <FormatQuoteIcon
                                    fontSize="small"
                                    sx={{ transform: 'scaleX(-1)' }}
                                    color="disabled"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={quote}
                                  primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default JourneyPainAnalysisTranscript;
